import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { OVERVIEW, VISION2030, FACTORY_IMAGES, GM_MESSAGE } from '../data/mock';
import { ArrowUpRight, TrendingUp, Quote } from 'lucide-react';

export const AboutSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [gmRef, gmInView] = useInView({ threshold: 0.1 });
  const [visionRef, visionInView] = useInView({ threshold: 0.1 });

  return (
    <>
      {/* About Section */}
      <section id="about" ref={ref} className="relative py-28 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <span className="text-[#D4952B] text-sm font-semibold tracking-[0.2em] uppercase">Company Overview</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mt-3 leading-tight">
              Who We Are
            </h2>
            <p className="text-2xl md:text-3xl text-[#0D1B2A]/30 font-semibold mt-2 font-['Noto_Sans_Arabic']">نظرة عامة على الشركة</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="space-y-6"
            >
              <p className="text-[#1C2B33]/80 text-lg leading-relaxed">
                {OVERVIEW.en}
              </p>
              <p className="text-[#1C2B33]/70 text-base leading-relaxed">
                {OVERVIEW.en2}
              </p>
              <div className="pt-4 border-t border-[#0D1B2A]/8">
                <p className="text-[#1C2B33]/50 text-sm leading-relaxed" dir="rtl">
                  {OVERVIEW.ar}
                </p>
              </div>
              <button
                onClick={() => document.querySelector('#heritage')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 text-[#0B3D5C] hover:text-[#D4952B] font-semibold text-sm transition-colors group mt-4"
              >
                Discover Our Heritage
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </motion.div>

            {/* Factory Images Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="grid grid-cols-2 gap-3"
            >
              {FACTORY_IMAGES.map((img, i) => (
                <div
                  key={i}
                  className={`relative rounded-2xl overflow-hidden group ${
                    i === 0 ? 'col-span-2 aspect-[16/9]' : 'aspect-square'
                  }`}
                >
                  <img
                    src={img}
                    alt={`SolarNova Factory ${i + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              ))}
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#0B3D5C] to-[#0D1B2A] flex flex-col items-center justify-center text-center p-6">
                <span className="text-[#E8B84A] text-3xl font-bold">100%</span>
                <span className="text-white/70 text-xs mt-1 font-medium tracking-wide">In-House<br/>Manufacturing</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GM Message Section */}
      <section ref={gmRef} className="relative py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={gmInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative bg-[#FAFAF7] rounded-3xl p-10 md:p-14 border border-[#0D1B2A]/5"
          >
            {/* Quote Icon */}
            <div className="absolute -top-5 left-10 w-10 h-10 rounded-full bg-[#D4952B] flex items-center justify-center shadow-lg shadow-amber-900/20">
              <Quote className="w-5 h-5 text-white" />
            </div>
            
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <p className="text-[#1C2B33]/75 text-base leading-relaxed italic">
                  "{GM_MESSAGE.en}"
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-12 h-1 bg-[#D4952B] rounded-full" />
                  <span className="text-[#0D1B2A] font-bold text-sm">{GM_MESSAGE.title}</span>
                </div>
              </div>
              <div className="border-t md:border-t-0 md:border-l border-[#0D1B2A]/8 pt-6 md:pt-0 md:pl-10">
                <p className="text-[#1C2B33]/50 text-sm leading-relaxed" dir="rtl">
                  "{GM_MESSAGE.ar}"
                </p>
                <div className="mt-6 flex items-center gap-3 justify-end" dir="rtl">
                  <div className="w-12 h-1 bg-[#D4952B] rounded-full" />
                  <span className="text-[#0D1B2A] font-bold text-sm font-['Noto_Sans_Arabic']">{GM_MESSAGE.titleAr}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision 2030 Section */}
      <section ref={visionRef} className="relative py-28 bg-[#0D1B2A] overflow-hidden">
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4952B]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#147DAB]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={visionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <span className="text-[#E8B84A] text-sm font-semibold tracking-[0.2em] uppercase">Strategic Alignment</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 leading-tight">
              Vision 2030
            </h2>
            <p className="text-2xl md:text-3xl text-white/30 font-semibold mt-2 font-['Noto_Sans_Arabic']">التوافق الاستراتيجي مع رؤية ٢٠٣٠</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={visionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-white/60 text-lg leading-relaxed"
            >
              {VISION2030.en}
            </motion.p>

            <div className="grid grid-cols-2 gap-4">
              {VISION2030.stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={visionInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="bg-white/5 border border-white/8 rounded-2xl p-6 hover:bg-white/8 transition-colors duration-500 group"
                >
                  <TrendingUp className="w-5 h-5 text-[#E8B84A] mb-3 group-hover:scale-110 transition-transform" />
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-white/40 text-xs font-medium tracking-wide">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
